import { clamp } from "lodash";

interface IWebsiteBuilderNormalizeValueOptionsBase<T> {
  value: unknown;
  defaultValue: T;
  required: boolean;
  validate?: (value: T) => boolean;
  transform?: (value: T) => T;
}

interface IWebsiteBuilderNormalizeStringOptions extends IWebsiteBuilderNormalizeValueOptionsBase<string> {
  type: "string";
}

interface IWebsiteBuilderNormalizeNumberOptions extends IWebsiteBuilderNormalizeValueOptionsBase<number> {
  type: "number";
}

interface IWebsiteBuilderNormalizeBooleanOptions extends IWebsiteBuilderNormalizeValueOptionsBase<boolean> {
  type: "boolean";
}

interface IWebsiteBuilderNormalizeObjectOptions extends IWebsiteBuilderNormalizeValueOptionsBase<object> {
  type: "object";
}

type TWebsiteBuilderNormalizeValueOptions =
  IWebsiteBuilderNormalizeStringOptions |
  IWebsiteBuilderNormalizeNumberOptions |
  IWebsiteBuilderNormalizeBooleanOptions |
  IWebsiteBuilderNormalizeObjectOptions;

type TWebsiteBuilderNormalizeValueType = TWebsiteBuilderNormalizeValueOptions["defaultValue"];

export function websiteBuilderNormalizeValue(options: IWebsiteBuilderNormalizeStringOptions): string;
export function websiteBuilderNormalizeValue(options: IWebsiteBuilderNormalizeNumberOptions): number;
export function websiteBuilderNormalizeValue(options: IWebsiteBuilderNormalizeBooleanOptions): boolean;
export function websiteBuilderNormalizeValue(options: IWebsiteBuilderNormalizeObjectOptions): object;
export function websiteBuilderNormalizeValue(options: TWebsiteBuilderNormalizeValueOptions): TWebsiteBuilderNormalizeValueType {
  const {
    type,
    value,
    required,
    defaultValue,
    validate,
    transform,
  } = options;

  if (value == null) {
    return required ? defaultValue : null;
  }

  const valueType = typeof value;

  if (valueType !== type) {
    return defaultValue;
  }

  if (
    (valueType === "number" && Number.isNaN(value as number)) ||
    (valueType === "number" && !Number.isFinite(value as number))
  ) {
    return defaultValue;
  }

  try {
    switch (type) {
      case "string": {
        if (validate && !validate(value as string)) {
          return defaultValue;
        } else {
          return transform ? transform(value as string) : value as string;
        }
      }
      case "number": {
        if (validate && !validate(value as number)) {
          return defaultValue;
        } else {
          return transform ? transform(value as number) : value as number;
        }
      }
      case "boolean": {
        if (validate && !validate(value as boolean)) {
          return defaultValue;
        } else {
          return transform ? transform(value as boolean) : value as boolean;
        }
      }
      case "object": {
        if (validate && !validate(value as object)) {
          return defaultValue;
        } else {
          return transform ? transform(value as object) : value as object;
        }
      }
      default: {
        return defaultValue;
      }
    }
  } catch {
    return defaultValue;
  }
}

export function websiteBuilderNormalizeHexColor(value: string, defaultValue: string, required: boolean): string {
  return websiteBuilderNormalizeValue({
    type: "string",
    value,
    defaultValue,
    required,
    validate: (v: string) => /^#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/.test(v),
  });
}

export function websiteBuilderNormalizeDatabaseId(value: number, defaultValue: number, required: boolean): number {
  return websiteBuilderNormalizeValue({
    type: "number",
    value,
    defaultValue,
    required,
    validate: (v: number) => v % 1 === 0 && v > 0,
  });
}

export function websiteBuilderNormalizeArray(value: object, defaultValue: object, required: boolean, elementType: "string" | "number" | "boolean"): object {
  return websiteBuilderNormalizeValue({
    type: "object",
    value,
    defaultValue,
    required,
    validate: (v: object) => Array.isArray(v),
    transform: (v: object) => {
      let obj: object = [];

      if (Array.isArray(v)) {
        obj = v.filter(item => {
          if (typeof item !== elementType) {
            return false;
          }
    
          if (typeof item === "number") {
            return !Number.isNaN(item) && Number.isFinite(item);
          } else {
            return true;
          }
        });
      }

      return obj;
    },
  });
}

export function websiteBuilderNormalizePercent(value: number, defaultValue: number, required: boolean): number {
  return (
    websiteBuilderNormalizeValue({
      type: "number",
      value,
      defaultValue,
      required,
      transform: (v: number) => clamp(v, 0, 100),
    })
  );
}